import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The Digio web SDK is loaded once from index.html and hangs off `window.Digio`.
 * The gateway it is loaded from decides the environment, so keep the two in
 * sync: `ext-gateway.digio.in` is sandbox, `app.digio.in` is production.
 */
const DIGIO_ENVIRONMENT = import.meta.env.VITE_DIGIO_ENVIRONMENT || 'production';

const DEFAULT_OPTIONS = {
  environment: DIGIO_ENVIRONMENT,
  is_iframe: true,
  is_redirection_approach: false
};

/**
 * Pull the Digio handoff out of a KYC session response.
 *
 * Every session-creating endpoint (initiate KYC, re-KYC, mobile/email update)
 * hands back the same `workflowDetails` block, but the shape reaching the
 * caller differs: raw responses keep the API's snake_case under `result`,
 * while parsed ones (parseInitiateKycResponse) are camelCase and flattened.
 * Accept both so callers never have to care which endpoint they came from.
 *
 * @param {Object} response - Raw or parsed KYC session response
 * @returns {{documentId: string, identifier: string, accessToken: string} | null}
 *          null when the response carries no Digio session
 */
export const resolveDigioWorkflow = (response) => {
  const result = response?.result ?? response ?? {};
  const workflow = result?.workflowDetails ?? response?.workflowDetails ?? {};
  const token = workflow?.accessToken ?? result?.accessToken ?? response?.accessToken ?? {};

  // `entity_id` and `kid` are the same document id; whichever arrives is fine.
  const documentId =
    token?.entity_id ??
    token?.entityId ??
    workflow?.kid ??
    result?.kid ??
    '';
  const accessToken = token?.id ?? '';
  const identifier =
    workflow?.customerIdentifier ??
    result?.customerIdentifier ??
    response?.customerIdentifier ??
    '';

  if (!documentId || !accessToken || !identifier) return null;

  return { documentId, accessToken, identifier };
};

/**
 * useDigioSdk - Opens the Digio KYC/e-sign overlay for a created session.
 *
 * Owns the SDK instance and tears it down on unmount so a half-finished
 * journey never leaves an orphaned iframe behind.
 *
 * @returns {{
 *   openDigio: Function,
 *   openDigioFromResponse: Function,
 *   closeDigio: Function,
 *   isDigioOpen: boolean
 * }}
 */
export const useDigioSdk = () => {
  const digioRef = useRef(null);
  const isMountedRef = useRef(true);
  const [isDigioOpen, setIsDigioOpen] = useState(false);

  const closeDigio = useCallback(() => {
    if (!digioRef.current) return;
    try {
      digioRef.current.cancel();
    } catch (error) {
      console.error('Failed to close Digio overlay:', error);
    }
    digioRef.current = null;
    if (isMountedRef.current) setIsDigioOpen(false);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      closeDigio();
    };
  }, [closeDigio]);

  /**
   * @param {Object} params
   * @param {string} params.documentId - Digio document / KID
   * @param {string} params.identifier - Customer mobile or email on the session
   * @param {string} params.accessToken - Gateway access token id
   * @param {Function} [params.onSuccess] - Journey finished inside the overlay
   * @param {Function} [params.onCancel] - Customer closed the overlay
   * @param {Function} [params.onError] - SDK missing, bad session, or failure
   * @returns {boolean} whether the overlay was launched
   */
  const openDigio = useCallback(({
    documentId,
    identifier,
    accessToken,
    onSuccess,
    onCancel,
    onError
  }) => {
    if (!documentId || !identifier || !accessToken) {
      onError?.(new Error('Incomplete Digio session details.'));
      return false;
    }

    if (!window?.Digio) {
      onError?.(new Error('Digio SDK is not available. Please refresh and try again.'));
      return false;
    }

    try {
      digioRef.current = new window.Digio({
        ...DEFAULT_OPTIONS,
        callback: (response) => {
          if (!response?.error_code) {
            closeDigio();
            onSuccess?.(response);
            return;
          }
          if (response?.error_code === 'CANCELLED') {
            closeDigio();
            onCancel?.(response);
            return;
          }
          closeDigio();
          onError?.(new Error(response?.message || 'Digio process failed.'), response);
        }
      });

      digioRef.current.init();
      digioRef.current.submit(documentId, identifier, accessToken);
      if (isMountedRef.current) setIsDigioOpen(true);
      return true;
    } catch (error) {
      console.error('Failed to launch Digio overlay:', error);
      digioRef.current = null;
      if (isMountedRef.current) setIsDigioOpen(false);
      onError?.(error);
      return false;
    }
  }, [closeDigio]);

  /**
   * Same as openDigio, but takes a KYC session response and figures the
   * document / identifier / token out of it.
   *
   * @param {Object} response - Raw or parsed KYC session response
   * @param {Object} [handlers] - onSuccess / onCancel / onError
   * @returns {boolean} whether the overlay was launched
   */
  const openDigioFromResponse = useCallback((response, handlers = {}) => {
    const workflow = resolveDigioWorkflow(response);
    if (!workflow) {
      handlers?.onError?.(new Error('No Digio session found in the response.'));
      return false;
    }
    return openDigio({ ...workflow, ...handlers });
  }, [openDigio]);

  return { openDigio, openDigioFromResponse, closeDigio, isDigioOpen };
};

export default useDigioSdk;
